import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import propz from 'propz';
import classNames from 'classnames';
import { TournamentAnnouncement } from 'Src/models/event';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import './Announce.scss';

interface Props {
  item: TournamentAnnouncement;
}

const getAttachmentUrl = (url: string): string => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^(https?:)?\/\//i.test(value) || value.indexOf('data:') === 0 || value.indexOf('blob:') === 0) return value;
  return `${window.apiBaseFile}${value}`;
};

export const Announce = (props: Props) => {
  const { item } = props;

  const [isShowMore, setShowMore] = useState<boolean>(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState<boolean>(false);
  const [announceBodyContentHeight, setAnnounceBodyContentHeight] = useState<number>(0);
  const announceBodyContentRef = useRef<HTMLDivElement | null>(null);

  const title = String(propz.get(item, ['title'], '') || '').trim();
  const body = String(propz.get(item, ['body'], '') || '');
  const attachments = propz.get(item, ['attachments'], {});
  const attachmentsImages = propz.get(attachments, ['images'], []);
  const attachmentsFiles = propz.get(attachments, ['files'], []);
  const announcePictureUrl = attachmentsImages.length > 0 ? getAttachmentUrl(attachmentsImages[0].url) : '';
  const isAttachmentsImagesExist = announcePictureUrl !== '';
  const isAttachmentsFilesExist = attachmentsFiles.length > 0;

  const bodyContent = body.includes('<body>')
    ? body.slice(body.lastIndexOf('<body>') + 6, body.indexOf('</body>'))
    : body;
  const announceHtml = bodyContent || body;
  const isBodyExist = announceHtml.trim() !== '';

  useEffect(() => {
    if (isBodyExist && announceBodyContentRef.current) {
      setAnnounceBodyContentHeight(announceBodyContentRef.current.clientHeight);
    }
  }, [isBodyExist, announceHtml]);

  const isGreaterThanSpecifiedHeight = announceBodyContentHeight > 220;

  const announceBodyClasses = classNames({
    eAnnounceBody: true,
    eAnnounceBodyOpen: isShowMore
  });

  const announceBodyContentClasses = classNames({
    eAnnounceBodyContent: true,
    eAnnounceBodyFullContent: isGreaterThanSpecifiedHeight
  });

  const renderAttachments = () => {
    if (!isAttachmentsFilesExist) {
      return null;
    }

    return (
      <div className="eAnnounceAttachments">
        <div className="eAnnounceAttachmentsTitle">Attachments</div>

        <div className="eAnnounceAttachmentsList">
          {attachmentsFiles.map(fileItem => {
            const { url, name, _id: itemKey } = fileItem;
            const link = getAttachmentUrl(url);
            const fileName = String(name || 'Attachment');

            return (
              <div key={itemKey} className="eAnnounceAttachmentCard">
                <div className="eAnnounceAttachmentMain">
                  <div className="eAnnounceAttachmentIcon">📎</div>

                  <div className="eAnnounceAttachmentMeta">
                    <div className="eAnnounceAttachmentName">{fileName}</div>
                    <div className="eAnnounceAttachmentHint">Open in a new tab or download to view the file.</div>
                  </div>
                </div>

                <div className="eAnnounceAttachmentActions">
                  <a className="btn btn-sm btn-outline-primary" href={link} download>
                    Download
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bAnnounce">
        <div className={classNames('eAnnounceMain', {'eAnnounceMain--withMedia': isAttachmentsImagesExist})}>
          {isAttachmentsImagesExist ? (
            <div className="eAnnounceMedia">
              <button
                type="button"
                className="eAnnounceImageThumbButton"
                onClick={() => setIsImagePreviewOpen(true)}
                title="Open image"
              >
                <img className="eAnnouncePicture" src={announcePictureUrl} alt={title || 'Announcement image'} />
                <span className="eAnnounceImageThumbOverlay">Open image</span>
              </button>
            </div>
          ) : null}

          <div className="eAnnounceContent">
            <div className="eAnnounceHeader">
              <div className="eAnnounceEyebrow">Announcement</div>
              <h4 className="eAnnounceTitle">{title || 'Untitled announcement'}</h4>
            </div>

            {isBodyExist ? (
              <div className={announceBodyClasses}>
                <div
                  className={announceBodyContentClasses}
                  ref={announceBodyContentRef}
                  dangerouslySetInnerHTML={{ __html: announceHtml }}
                />

                {isGreaterThanSpecifiedHeight ? (
                  <div className="eAnnounceFooterActions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowMore(!isShowMore)}
                    >
                      {isShowMore ? 'Show less' : 'Show more'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {renderAttachments()}
          </div>
        </div>
      </div>

      {isAttachmentsImagesExist ? (
        <SimpleModal
          isOpen={isImagePreviewOpen}
          onCloseClick={() => setIsImagePreviewOpen(false)}
          title={title || 'Image preview'}
          showFooter={false}
          customClass="eAnnouncePreviewDialog"
        >
          <div className="eAnnouncePreviewModal">
            <img className="eAnnouncePreviewImage" src={announcePictureUrl} alt={title || 'Announcement image'} />
          </div>
        </SimpleModal>
      ) : null}
    </>
  );
};

Announce.displayName = 'Announce';

